/**
 * IMGLIB-1 · 图片库接口（后端 routes/media_library.py）。
 * 契约：designs/mockups/image-library/图片库-样稿.html「说明 / API」页（tag image-library-mockup-v1）。
 */
import { api, apiConfig, ApiError, tryRefresh } from "./api";
import { useAuthStore } from "@/store/authStore";

export type MediaKind = "gallery" | "highlight";            // 相册（横版）/ 宣传图（竖版长图）
export type MediaCategory = "city" | "vehicle" | "team";    // 不属于某个景点的图：城市风光 / 车辆 / 团队
export const CATEGORY_LABEL: Record<MediaCategory, string> = { city: "城市风光", vehicle: "车辆", team: "团队" };
export const KIND_LABEL: Record<MediaKind, string> = { gallery: "相册（横版）", highlight: "宣传图（竖版长图）" };
export const KIND_SHORT: Record<MediaKind, string> = { gallery: "相册 · 横版", highlight: "宣传图 · 竖版" };
export const MIN_WIDTH: Record<MediaKind, number> = { gallery: 1280, highlight: 750 };
/** 单张上限：和后端 services/media_library.py 的 MAX_BYTES 同值（测试里对读后端源码，改一边不改另一边会红）；nginx /api/ 上限 12 MB，一次一张远低于它。 */
export const MAX_BYTES = 7 * 1024 * 1024;
export const MAX_MB = MAX_BYTES / 1024 / 1024;

export interface MediaAsset {
  id: string;
  url: string;
  /** 地点-景点-序号，后端现拼（地点 / 景点改名自动跟着变） */
  name: string;
  kind: MediaKind;
  kind_label: string;
  place_id: string | null;
  place_name: string | null;
  attraction_id: string | null;
  attraction_name: string | null;
  category: MediaCategory | null;
  category_label: string | null;
  seq: number | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  classified: boolean;
  /** 合格 = 尺寸读得到且方向 / 宽度满足该类型要求；读不到尺寸 = null（未检测） */
  qualified: boolean | null;
  usage_count: number;
  /** 实拍授权三态：true 是 / false 否 / null 未声明（后端恒带；企业版界面不读） */
  licensed?: boolean | null;
}

export interface MediaUse {
  product_id: string;
  code: string;
  name_cn: string;
  slot: MediaKind;
  slot_label: string;
  position: number;
  is_cover: boolean;
}
export interface MediaAssetDetail extends MediaAsset {
  original_filename: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  created_at: string | null;
  used_in: MediaUse[];
}

export interface LibraryTabs {
  all: number;
  unclassified: number;
  places: { place_id: string; name: string; count: number }[];
}
export interface LibraryList { total: number; items: MediaAsset[]; tabs: LibraryTabs }

export interface LibraryFilters {
  place?: string;
  attraction?: string;
  /** 多个景点 id，逗号分隔（从产品里选图时「本线行程里的景点」） */
  attractions?: string;
  category?: MediaCategory;
  kind?: MediaKind;
  usage?: "used" | "unused" | "unclassified";
  q?: string;
  /** 实拍授权筛选：yes / no / undeclared */
  licensed?: string;
  limit?: number;
  offset?: number;
}

export interface UploadResult { ok: boolean; filename: string; media_id?: string; name?: string; reason?: string | null; width?: number | null; height?: number | null }
export interface UploadResponse { results: UploadResult[]; uploaded: number; rejected: number }

export interface ProductImages {
  highlights: MediaAsset[];
  gallery: MediaAsset[];
  cover_url: string | null;
  gallery_qualified: number;
  gallery_recommended: number;
  highlights_qualified: number;
}

export interface Place {
  id: string;
  name_cn: string;
  name_en: string | null;
  status: "active" | "disabled";
  sort: number;
  attraction_count: number;
  image_count: number;
}

export const MEDIA_QK = {
  library: (f: LibraryFilters) => ["media-library", "list", f] as const,
  libraryAll: ["media-library"] as const,
  asset: (id: string) => ["media-library", "asset", id] as const,
  places: ["settings", "places"] as const,
  productImages: (pid: string) => ["products", "detail", pid, "images"] as const,
};

export function listLibrary(f: LibraryFilters = {}): Promise<LibraryList> {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== "") p.set(k, String(v)); });
  const qs = p.toString();
  return api(`/api/v1/media-library${qs ? `?${qs}` : ""}`);
}
export const getAsset = (id: string): Promise<MediaAssetDetail> => api(`/api/v1/media-library/${id}`);
export const reclassifyAsset = (id: string, body: { place_id: string | null; attraction_id?: string | null; category?: MediaCategory | null }): Promise<MediaAsset> =>
  api(`/api/v1/media-library/${id}`, { method: "PATCH", body: JSON.stringify(body) });
export const deleteAsset = (id: string): Promise<{ deleted: boolean }> => api(`/api/v1/media-library/${id}`, { method: "DELETE" });

/** multipart 请求（api() 固定发 JSON 头，multipart 要让浏览器带 boundary）；401 刷新 token 重试一次，错误转成 ApiError（detail 原样保留，409 要读在用产品列表）。 */
async function postForm<T>(path: string, fd: FormData): Promise<T> {
  const doPost = () => {
    const token = useAuthStore.getState().accessToken;
    const tenantSlug = useAuthStore.getState().user?.tenantSlug;
    return fetch(`${apiConfig.base}${path}`, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(tenantSlug ? { "X-Tenant-Slug": tenantSlug } : {}) },
      body: fd,
    });
  };
  let res = await doPost();
  if (res.status === 401 && useAuthStore.getState().refreshToken) {
    if (await tryRefresh()) res = await doPost();
  }
  if (!res.ok) {
    let body: { detail?: unknown } | null = null;
    try { body = await res.json(); } catch { /* 非 JSON */ }
    const d = body?.detail;
    throw new ApiError(res.status, typeof d === "string" ? d : (d as { message?: string } | undefined)?.message ?? res.statusText, body);
  }
  return res.json();
}

export interface UploadParams {
  files: File[];
  kind: MediaKind;
  place_id: string;
  attraction_id?: string | null;
  category?: MediaCategory | null;
  /** 从产品里打开上传时带上：传完自动挂进这条线对应的位置 */
  product_id?: string;
}
export function uploadToLibrary(p: UploadParams): Promise<UploadResponse> {
  const fd = new FormData();
  p.files.forEach((f) => fd.append("files", f, f.name));
  fd.append("kind", p.kind);
  fd.append("place_id", p.place_id);
  if (p.attraction_id) fd.append("attraction_id", p.attraction_id);
  if (p.category) fd.append("category", p.category);
  if (p.product_id) fd.append("product_id", p.product_id);
  return postForm("/api/v1/media-library", fd);
}
/**
 * IMGUP-1 · 一次只发一张：每个请求只带 1 个文件 + 同一批共用的归类字段。
 * 为什么：一个请求塞多张时总量超过 nginx /api/ 的 12 MB 上限就被 413 整批挡掉；一张一张发每个请求都远低于上限，失败也只影响那一张。
 * 后端接口不变（本来就收 1~N 张）；序号 / 挂进产品由后端按请求顺序加锁分配，并发 ≤ 2 安全。
 */
export function uploadOneToLibrary(file: File, p: Omit<UploadParams, "files">): Promise<UploadResponse> {
  return uploadToLibrary({ ...p, files: [file] });
}

/** 上传失败 → 说人话（同事看得懂、知道下一步）。413 = 被 nginx 挡；非 ApiError = 网络断了 / 请求没发出去；其余照后端返回的原因。 */
export function uploadFailReason(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 413) return `这张图太大，传不上去（单张请小于 ${MAX_MB} MB）`;
    return errMessage(e, "上传失败，点重试");
  }
  return "网络中断，点重试";
}

/** 简单的并发池：按顺序取任务，同时最多跑 limit 个；每个任务自己处理错误（不应抛出）。 */
export async function runPool<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const i = next++;
      await worker(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
}
export const UPLOAD_CONCURRENCY = 2;

export function replaceAssetFile(id: string, file: File): Promise<MediaAsset> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  return postForm(`/api/v1/media-library/${id}:replace`, fd);
}

export const getProductImages = (pid: string): Promise<ProductImages> => api(`/api/v1/products/${pid}/images`);
export const putProductImages = (pid: string, body: { highlights: string[]; gallery: string[] }): Promise<ProductImages> =>
  api(`/api/v1/products/${pid}/images`, { method: "PUT", body: JSON.stringify(body) });

export const listPlaces = (): Promise<Place[]> => api("/api/v1/settings/places");
export const createPlace = (body: { name_cn: string; name_en?: string | null }): Promise<Place> =>
  api("/api/v1/settings/places", { method: "POST", body: JSON.stringify(body) });
export const patchPlace = (id: string, body: Partial<{ name_cn: string; name_en: string | null; status: "active" | "disabled"; sort: number }>): Promise<Place> =>
  api(`/api/v1/settings/places/${id}`, { method: "PATCH", body: JSON.stringify(body) });
export const deletePlace = (id: string): Promise<{ deleted: boolean }> => api(`/api/v1/settings/places/${id}`, { method: "DELETE" });

/** 前端先拦一道（和后端同一口径，给上传弹窗预判用；最终以后端为准）。读不到尺寸（浏览器解码失败）= 交给后端判。 */
export function precheck(kind: MediaKind, file: { name: string; size: number }, dims: { w: number; h: number } | null): string | null {
  if (!/\.(jpe?g|png|webp)$/i.test(file.name)) return "只收 jpg / png / webp 图片";
  if (file.size > MAX_BYTES) return `超过 ${MAX_MB} MB（${(file.size / 1024 / 1024).toFixed(1)} MB）`;
  if (!dims) return null;
  const { w, h } = dims;
  if (kind === "gallery") {
    if (h >= w) return "竖版图不能当相册用（相册要横版）";
    if (w < MIN_WIDTH.gallery) return `宽不够 ${MIN_WIDTH.gallery}，相册用不了`;
  } else {
    if (w >= h) return "横版图不能当宣传图用（宣传图要竖版长图）";
    if (w < MIN_WIDTH.highlight) return `宽不够 ${MIN_WIDTH.highlight}，宣传图用不了`;
  }
  return null;
}

/** 上传弹窗预览「地点-景点-序号」用：这个归类下的下一个序号 + 名字前缀 */
export function nextName(p: { place_id: string; attraction_id?: string | null; category?: MediaCategory | null }): Promise<{ next_seq: number; prefix: string }> {
  const qs = new URLSearchParams({ place_id: p.place_id });
  if (p.attraction_id) qs.set("attraction_id", p.attraction_id);
  if (p.category) qs.set("category", p.category);
  return api(`/api/v1/media-library/next-seq?${qs.toString()}`);
}

/** 后端错误说明：detail 可能是字符串，也可能是 {message, …}（api() 只会把字符串型 detail 放进 e.message），统一取出人话 */
export function errMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const d = (e.detail as { detail?: unknown } | undefined)?.detail;
    if (typeof d === "string" && d) return d;
    if (d && typeof d === "object" && typeof (d as { message?: unknown }).message === "string") return (d as { message: string }).message;
    if (e.message && e.message !== "Bad Request") return e.message;
  }
  return fallback;
}
